<template>
    <ag-grid-vue
        :style="`width: 100%; height: ${height}`"
        :class="`ag-theme-${size === 'sm'?'balham':'alpine'}${store.state.isDark?'-dark':''}`"
        :rowData="rowData"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        rowSelection="single"
        @selection-changed="onSelectionChanged"
        :animateRows="animateRows"
        :pagination="pagination"
        :paginationAutoPageSize="true"
        :gridOptions="gridOptions"
        :getRowStyle="getRowStyle"
        :localeText="localeText"
        :suppressDragLeaveHidesColumns="true">
    </ag-grid-vue>
</template>

<script>
import { computed, nextTick, onBeforeMount, onMounted, reactive, ref, watchEffect } from 'vue'
import { AgGridVue } from 'ag-grid-vue3';
import localeTextJson from './localeText.json'
import { useStore } from 'vuex'
import { formatNumber } from '../../utils/formatNumber'
import { formatDate } from '../../utils/formatDate';
import { calcSummary } from '../../utils/calcSummary'

export default {
    name: 'tw-grid-view-ag',
    components: {
        AgGridVue
    },
    setup(props, context) {
        const gridOptions = ref(null)
        var gridApi = null;

        const getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };

        const localeText = reactive(localeTextJson)

        // 主题
        const store = useStore()

        onBeforeMount(() => {
            gridOptions.value = {};
        })

        onMounted(() => {
            gridApi = gridOptions.value.api;
            
            watchEffect(() => {
                if(props.data_source.current) {
                    nextTick(() => {
                        gridApi.forEachNodeAfterFilter(node => {
                            if(node.data === props.data_source.current){
                                node.setSelected(true);
                            }
                        });
                    })
                }
                if(props.data_source.datas) {
                    pinnedBottomRowData()
                }
            })
        })

        const columnDefs = computed(() => {
            return props.data_source && props.data_source.columns && props.data_source.columns.filter(el => {
                let visible = true
                // 需要注意如果无 visible 时也要显示，只有等于 False 时才不显示
                if (el.visible === false) {
                    visible = false
                } else {
                    // 如果指定分组，则只显示该分组的字段
                    if (props.group_name) {
                        visible = el.group_name === props.group_name
                    }
                }

                el['field'] = el.field_name
                el['headerName'] = el.caption
                el['width'] = el.edit_width || ''
                el['pinned'] = el?.fixed
                // 自动换行
                el['cellRenderer'] = (params) => {
                    return params?.value?.toString()?.replace(/\r\n/g,"<br>") ?? ''
                }

                if(el.display_format){
                    switch (el.data_type) {
                        case 'number': 
                            el['valueFormatter'] = numberFormatter;
                            break;
                        case 'data': 
                            el['valueFormatter'] = dateFormatter;
                            break;
                        default: break;
                    }
                }

                return visible
            }) || []
        })

        const rowData = computed(() => {
            if (props.data_source && Array.isArray(props.data_source.datas)) {
                return props.data_source.datas.filter(item => item)
            } else {
                return []
            }
        })

        const showSummary = computed(() => {
            return columnDefs.value.filter(el => !!el.summary_type).length !== 0
        })

        const handleCurrentChange = function(val) {
            if (val) {
                // eslint-disable-next-line vue/no-mutating-props
                props.data_source.current = val
            }
        }

        const defaultColDef = reactive({
            sortable: props.sortable,
            filter: props.filter,
            resizable: props.resizable,
            lockPosition: props.lock,
            autoHeight: true
        })

        const pinnedBottomRowData = () => {
            nextTick(() => {
                if(gridApi){
                    const datas = [];
                    gridApi.forEachNodeAfterFilter(node => {
                        datas.push(node.data);
                    });

                    const summaryColumns = columnDefs.value.filter(el => !!el.summary_type);
                    if (summaryColumns.length && datas.length) {
                        const pinned = [];
                        const _summary = {};

                        summaryColumns.forEach(column => {
                            _summary[column.field_name] = calcSummary(datas, column);
                        });
                        pinned.push(_summary);
                        gridApi.setPinnedBottomRowData(pinned)
                    }else{
                        gridApi.setPinnedBottomRowData([])
                    }
                }
            })
        }

        const onSelectionChanged = () => {
            var selectedRows = gridApi.getSelectedRows();
            const temp = props.data_source;

            temp.current = selectedRows[0] ?? null
            context.emit('update:data_source', temp)
        }

        // 数字格式化
        const numberFormatter = (params) => {
            return formatNumber(params.value, params.colDef.display_format)
        }
        // 日期格式化
        const dateFormatter = (params) => {
            return formatDate(params.value, params.colDef.display_format)
        }

        return {
            columnDefs,
            rowData,
            showSummary,
            defaultColDef,
            handleCurrentChange,
            gridOptions,
            getRowStyle,
            localeText,
            store,
            onSelectionChanged
        }
    },
    props: {
        data_source: {
            type: Object,
            default: function() {
                return { }
            }
        },
        group_name: {
            type: String,
            default: ''
        },
        pagination: {
            type: Boolean,
            default: false
        },
        height: {
            type: String,
            default: '300px'
        },
        size: {
            type: String,
            default: 'sm'
        },
        sortable: {
            type: Boolean,
            default: true
        },
        filter: {
            type: Boolean,
            default: true
        },
        resizable: {
            type: Boolean,
            default: true
        },
        lock: {
            type: Boolean,
            default: false
        },
        animateRows: {
            type: Boolean,
            default: true
        }
    }
}
</script>

<style>

</style>